/**
 * JOB HISTORY SCREEN
 *
 * Shows completed deliveries with payout, date, addresses.
 * Period filters: Today, This Week, This Month, All Time.
 * Data comes from backend API, with Redux as fallback.
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
  View, Text, StyleSheet, FlatList, TouchableOpacity, Platform,
  ActivityIndicator, RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { colors, spacing, radii, fonts, RD } from '../../theme/colors';
import { api } from '../../services/api';
import { RootState } from '../../store';

type Period = 'today' | 'week' | 'month' | 'all';

const PERIOD_LABELS: Record<Period, string> = {
  today: 'Today',
  week: 'This Week',
  month: 'This Month',
  all: 'All Time',
};

function isInPeriod(dateStr: string | undefined, period: Period): boolean {
  if (!dateStr) return period === 'all';
  const date = new Date(dateStr);
  const now = new Date();

  if (period === 'all') return true;

  if (period === 'today') {
    return (
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate()
    );
  }

  if (period === 'week') {
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    return date >= startOfWeek;
  }

  if (period === 'month') {
    return (
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth()
    );
  }

  return true;
}

export function JobHistoryScreen({ navigation }: any) {
  const reduxJobs = useSelector((s: RootState) => s.jobs.completedJobs);
  const reduxEarnings = useSelector((s: RootState) => s.jobs.earnings);
  const [apiJobs, setApiJobs] = useState<any[]>([]);
  const [apiEarnings, setApiEarnings] = useState<{ totalEarnings: number; completedCount: number } | null>(null);
  const [period, setPeriod] = useState<Period>('all');
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);

  // Merge: use API jobs as primary, Redux as fallback
  const completedJobs = apiJobs.length > 0 ? apiJobs : reduxJobs;
  const earnings = apiEarnings || reduxEarnings;

  const loadFromApi = useCallback(async () => {
    try {
      const [jobsRes, earningsRes] = await Promise.all([
        api.get('/drivers/me/jobs').catch(() => ({ data: [] })),
        api.get('/earnings').catch(() => ({ data: null })),
      ]);
      // A job is delivered/done once the driver finishes delivery
      // (BUYER_SIGNATURE_COMPLETE); admin COMPLETED comes later. Show both.
      const DELIVERED_STATUSES = ['BUYER_SIGNATURE_COMPLETE', 'COMPLETED'];
      const jobs = (jobsRes.data || []).filter((j: any) => DELIVERED_STATUSES.includes(j.status));
      if (jobs.length > 0) setApiJobs(jobs);
      if (earningsRes.data) {
        setApiEarnings({
          totalEarnings: earningsRes.data.totalPaid || earningsRes.data.totalEarnings || 0,
          completedCount: earningsRes.data.completedCount || jobs.length,
        });
      }
    } catch {}
  }, []);

  useEffect(() => {
    loadFromApi().finally(() => setLoading(false));
  }, [loadFromApi]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await loadFromApi();
    await new Promise(r => setTimeout(r, 400));
    setRefreshing(false);
  }, [loadFromApi]);

  const filtered = useMemo(() => {
    // completedAt is often unset on API jobs — fall back to deliveredAt/updatedAt (same chain
    // as EarningsScreen) so delivered jobs don't silently vanish from Today/Week/Month.
    const jobs = completedJobs.filter((j: any) => isInPeriod(j.completedAt ?? j.deliveredAt ?? j.updatedAt, period));
    return [...jobs].reverse();
  }, [completedJobs, period]);

  const periodEarnings = useMemo(() => {
    return filtered.reduce((sum: number, j: any) => sum + Math.round(j.payoutAmount ?? j.payout ?? j.amount ?? 0), 0);
  }, [filtered]);

  return (
    <View style={s.container}>
      {/* Header */}
      <View style={s.header}>
        <Text style={s.headerTitle}>Job History</Text>
        <Text style={s.headerSub}>{completedJobs.length} completed</Text>
      </View>

      {/* Tap to refresh */}
      {Platform.OS === 'web' && !refreshing && (
        <TouchableOpacity style={s.refreshRow} onPress={onRefresh} activeOpacity={0.7}>
          <Ionicons name="refresh-outline" size={14} color={RD.textFaint} />
          <Text style={s.refreshText}>Tap to refresh</Text>
        </TouchableOpacity>
      )}
      {refreshing && (
        <View style={s.refreshRow}>
          <ActivityIndicator size="small" color={RD.ink} />
          <Text style={s.refreshText}>Refreshing...</Text>
        </View>
      )}

      {/* Earnings Card with Period Filter */}
      <View style={s.earningsCard}>
        <Text style={s.earningsLabel}>Earnings</Text>
        <Text style={s.earningsAmount}>${periodEarnings.toFixed(0)}</Text>
        <View style={s.periodRow}>
          {(['today', 'week', 'month', 'all'] as Period[]).map(p => (
            <TouchableOpacity
              key={p}
              style={[s.periodBtn, period === p && s.periodBtnActive]}
              onPress={() => setPeriod(p)}
            >
              <Text style={[s.periodText, period === p && s.periodTextActive]}>
                {PERIOD_LABELS[p]}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Summary — updates with selected period */}
      <View style={s.summaryRow}>
        <View style={s.summaryCard}>
          <Ionicons name="cash-outline" size={20} color={RD.money} />
          <Text style={s.summaryValueMoney}>${periodEarnings.toFixed(0)}</Text>
          <Text style={s.summaryLabel}>{period === 'all' ? 'Total Earned' : `${PERIOD_LABELS[period]} Earned`}</Text>
        </View>
        <View style={s.summaryCard}>
          <Ionicons name="checkmark-circle-outline" size={20} color={RD.money} />
          <Text style={s.summaryValue}>{filtered.length}</Text>
          <Text style={s.summaryLabel}>Deliveries</Text>
        </View>
      </View>

      {/* Filtered count */}
      <View style={s.listHeader}>
        <Text style={s.listHeaderTitle}>{PERIOD_LABELS[period]}</Text>
        <Text style={s.listHeaderCount}>{filtered.length} {filtered.length === 1 ? 'job' : 'jobs'}</Text>
      </View>

      {/* Job List */}
      {filtered.length === 0 ? (
        <View style={s.emptyWrap}>
          <Ionicons name="document-outline" size={48} color={RD.g30} />
          <Text style={s.emptyTitle}>No Jobs {period !== 'all' ? 'in This Period' : 'Yet'}</Text>
          <Text style={s.emptyText}>
            {period !== 'all'
              ? 'Try selecting a different time period.'
              : 'Your delivery history will appear here after you complete your first job.'}
          </Text>
        </View>
      ) : (
        <FlatList
          data={filtered}
          keyExtractor={(item, index) => item.id || String(item.wooOrderId ?? `job-${index}`)}
          contentContainerStyle={s.list}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={RD.ink} />}
          renderItem={({ item }) => (
            <TouchableOpacity
              style={s.jobCard}
              onPress={() => navigation.navigate('JobDetail', { jobId: item.id, job: item })}
              activeOpacity={0.8}
            >
              <View style={s.jobLeft}>
                <View style={s.jobIconWrap}>
                  <Ionicons name="checkmark" size={16} color={RD.money} />
                </View>
                <View style={s.jobInfo}>
                  <Text style={s.jobTitle} numberOfLines={1}>
                    {item.productTitle || item.displayTitle || (item.orderNumber ? `Order #${item.orderNumber}` : 'Job')}
                  </Text>
                  <Text style={s.jobMeta}>
                    {item.completedAt
                      ? new Date(item.completedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                      : 'Completed'}
                  </Text>
                  <Text style={s.jobAddress} numberOfLines={1}>
                    {item.pickupAddress ? `${truncate(item.pickupAddress, 25)} → ${truncate(item.deliveryAddress, 25)}` : ''}
                  </Text>
                </View>
              </View>
              <View style={s.jobRight}>
                <Text style={s.jobPayout}>${Math.round(item.payoutAmount ?? 0)}</Text>
                <View style={s.statusBadge}>
                  <Text style={s.statusText}>DONE</Text>
                </View>
              </View>
            </TouchableOpacity>
          )}
        />
      )}
    </View>
  );
}

function truncate(str: string, max: number) {
  if (!str) return '';
  return str.length > max ? str.slice(0, max) + '...' : str;
}

const s = StyleSheet.create({
  container:       { flex: 1, backgroundColor: RD.bg },
  header:          { paddingHorizontal: spacing.lg, paddingTop: Platform.OS === 'ios' ? 60 : 40, paddingBottom: spacing.sm },
  headerTitle:     { fontSize: 30, fontWeight: '700', color: RD.text, fontFamily: fonts.heavy, letterSpacing: -0.5 },
  headerSub:       { fontSize: 14, color: RD.textFaint, marginTop: 4 },
  refreshRow:      { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 6, paddingVertical: 6 },
  refreshText:     { fontSize: 12, color: RD.textFaint, fontWeight: '500' },

  // Earnings card with period selector (total paid)
  earningsCard:    { backgroundColor: RD.moneySoft, borderRadius: 18, padding: spacing.xl, marginHorizontal: spacing.lg, marginBottom: spacing.lg, borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  earningsLabel:   { fontSize: 12, color: RD.textSec, fontWeight: '700', marginBottom: 4, letterSpacing: 1.5, textTransform: 'uppercase' },
  earningsAmount:  { fontSize: 42, fontWeight: '700', color: RD.money, marginBottom: spacing.md, fontFamily: fonts.heavy, letterSpacing: -1.5 },
  // Segmented control
  periodRow:       { flexDirection: 'row', backgroundColor: RD.g05, borderRadius: 13, padding: 4 },
  periodBtn:       { flex: 1, alignItems: 'center', paddingVertical: 8, borderRadius: 10 },
  periodBtnActive: { backgroundColor: RD.ink },
  periodText:      { fontSize: 12, fontWeight: '600', color: RD.textSec },
  periodTextActive:{ color: '#fff' },

  summaryRow:      { flexDirection: 'row', gap: 10, paddingHorizontal: spacing.lg, marginBottom: spacing.md },
  summaryCard:     { flex: 1, backgroundColor: RD.surface, borderRadius: 18, padding: 16, gap: 6, alignItems: 'center', borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  summaryValue:    { fontSize: 22, fontWeight: '700', color: RD.text, letterSpacing: -0.5 },
  summaryValueMoney:{ fontSize: 22, fontWeight: '700', color: RD.money, letterSpacing: -0.5 },
  summaryLabel:    { fontSize: 10, color: RD.textFaint, fontWeight: '700', letterSpacing: 0.5, textTransform: 'uppercase' },

  listHeader:      { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingHorizontal: spacing.lg, marginBottom: spacing.sm },
  listHeaderTitle: { fontSize: 16, fontWeight: '700', color: RD.text },
  listHeaderCount: { fontSize: 13, color: RD.textFaint },

  list:            { paddingHorizontal: spacing.lg, paddingBottom: Platform.OS === 'ios' ? 110 : 88 },
  jobCard:         { backgroundColor: RD.surface, borderRadius: 18, padding: 16, flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, borderWidth: 1, borderColor: RD.border, shadowColor: '#000', shadowOpacity: 0.04, shadowRadius: 3, elevation: 1 },
  jobLeft:         { flexDirection: 'row', alignItems: 'center', flex: 1, gap: 12 },
  jobIconWrap:     { width: 36, height: 36, borderRadius: 18, backgroundColor: RD.moneySoft, justifyContent: 'center', alignItems: 'center' },
  jobInfo:         { flex: 1 },
  jobTitle:        { fontSize: 14, fontWeight: '600', color: RD.text },
  jobMeta:         { fontSize: 12, color: RD.textFaint, marginTop: 2 },
  jobAddress:      { fontSize: 11, color: RD.textFaint, marginTop: 2 },
  jobRight:        { alignItems: 'flex-end', gap: 4 },
  jobPayout:       { fontSize: 18, fontWeight: '700', color: RD.money },
  statusBadge:     { backgroundColor: RD.moneySoft, borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2 },
  statusText:      { fontSize: 10, fontWeight: '700', color: RD.money },

  emptyWrap:       { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl },
  emptyTitle:      { fontSize: 18, fontWeight: '700', color: RD.text, marginTop: spacing.md },
  emptyText:       { fontSize: 14, color: RD.textFaint, textAlign: 'center', lineHeight: 20, marginTop: 6 },
});
